/**
 * usePendingAction
 * Hook to check and execute pending actions after auth/validation
 * Call this in components that might be return destinations (e.g., ProfessionalDetailPage)
 *
 * TODO: Implement proper error handling and fallback for pending action execution.
 * Currently, failed action execution is not caught or retried. Future enhancement should include:
 * - Try-catch blocks around action execution
 * - Retry logic with exponential backoff
 * - User notification on execution failure
 * - Logging for debugging
 */

import { useAuth } from '@shared/contexts'
import { apiClient } from '@shared/lib/api'
import { API_ENDPOINTS } from '@shared/lib/api/endpoints'
import { analytics, buildWhatsappContactProps, EVENTS, WHATSAPP_STAGE, WHATSAPP_VIA } from '@shared/lib/analytics'
import { actionStorage, PendingAction } from '@shared/lib/storage'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { logger } from '@shared/lib/utils/logger'

export function usePendingAction() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isAuthenticated } = useAuth()

  useEffect(() => {
    let isMounted = true

    const processAction = () => {
      // Only process if authenticated
      if (!isAuthenticated || !user) return

      // Check for pending action
      const pendingAction = actionStorage.getPendingAction()
      if (!pendingAction) return

      // Check if we're on the return path
      if (location.pathname !== pendingAction.returnPath) {
        void navigate(pendingAction.returnPath, { replace: true })
        return
      }

      // We're on the return path - check validation status
      // If not validated, redirect to profile page
      // ProfilePage will detect pending action and show validation alert
      if (!user.isAccountValidated) {
        void navigate('/profile', { replace: true })
        return
      }

      // All good - execute the pending action
      void executePendingAction(pendingAction).then(() => {
        if (isMounted) actionStorage.clearPendingAction()
      }).catch((error: unknown) => {
        logger.error('Error executing pending action:', error)
      })
    }

    processAction()

    return () => {
      isMounted = false
    }
  }, [isAuthenticated, user, location.pathname, navigate])

  /**
   * Execute the pending action based on type
   */
  const executePendingAction = async (action: PendingAction) => {
    switch (action.type) {
      case 'contact_professional': {
        // professionName may be absent in actions stored before this deploy — cast reflects that
        const { professionalId, professionalName, professionName, contactAttemptId } =
          action.data as {
            professionalId?: string
            professionalName?: string
            professionName?: string
            contactAttemptId?: string
          }
        if (!professionalId) {
          // Corrupted/legacy pending action: still emit a failed event so this path's funnel
          // doesn't silently disagree with the direct path. professional_id is unknown here.
          analytics.capture(EVENTS.WHATSAPP_CONTACT, {
            ...buildWhatsappContactProps({
              professional_id: '',
              professional_name: professionalName,
              profession_name: professionName,
              contact_attempt_id: contactAttemptId,
            }, WHATSAPP_VIA.PENDING_ACTION),
            stage: WHATSAPP_STAGE.FAILED,
            reason: 'no_professional_id',
          })
          toast.error('No pudimos obtener el número del profesional.')
          return
        }
        const trackProps = buildWhatsappContactProps({
          professional_id: professionalId,
          professional_name: professionalName,
          profession_name: professionName,
          contact_attempt_id: contactAttemptId ?? `${professionalId}:${action.timestamp}`,
        }, WHATSAPP_VIA.PENDING_ACTION)
        try {
          const { contact } = await apiClient.get<{ contact: string }>(
            API_ENDPOINTS.professionals.contactNumber(professionalId)
          )
          if (!contact) {
            analytics.capture(EVENTS.WHATSAPP_CONTACT, { ...trackProps, stage: WHATSAPP_STAGE.FAILED, reason: 'no_contact' })
            toast.error('No pudimos obtener el número del profesional.')
            return
          }
          if (action.message) toast.info(action.message)
          const cleanPhone = contact.replace(/\D/g, '')
          const message = encodeURIComponent(
            `Hola ${professionalName || 'Profesional'}, vi tu perfil en ProFessionals y me gustaría contactarte.`
          )
          // Broad metric: reaching window.open counts as a contact attempt (stage:'opened').
          // We don't distinguish popup-blocked here — this resumes after a redirect with no
          // user gesture, so block detection is meaningless. See analytics.ts catalogue.
          window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank')
          analytics.capture(EVENTS.WHATSAPP_CONTACT, { ...trackProps, stage: WHATSAPP_STAGE.OPENED })
        } catch (err) {
          analytics.capture(EVENTS.WHATSAPP_CONTACT, { ...trackProps, stage: WHATSAPP_STAGE.FAILED, reason: 'api_error' })
          toast.error('No pudimos abrir WhatsApp. Probá de nuevo.')
          logger.error('contactNumber fetch failed in pending action', err)
        }
        break
      }
      case 'create_review': {
        // Dispatch event for review components to open the review dialog
        window.dispatchEvent(
          new CustomEvent('open-review-dialog', {
            detail: action
          })
        )
        break
      }
      case 'edit_review':
      case 'delete_review': {
        // These will be handled by the review components
        // Just dispatch a custom event that they can listen to
        window.dispatchEvent(
          new CustomEvent('execute-pending-action', {
            detail: action
          })
        )
        break
      }
      default: {
        logger.warn('Unknown pending action type:', action.type)
      }
    }
  }
}
